/**
 * Natural sort function that handles mixed alphanumeric strings
 * Sorts strings with numbers in a human-friendly way where:
 * - Numbers are compared numerically (1 < 2 < 10)
 * - Text is compared alphabetically
 * - Numbers come before text when comparing different types
 *
 * @param a First string to compare
 * @param b Second string to compare
 * @returns Negative if a comes before b, positive if b comes before a, 0 if equal
 */
export const compareAlphaNum = (a: string, b: string): number => {
  // Split the strings into parts that are either all letters or all numbers
  const aParts = a.match(/[a-zA-Z]+|\d+/g) || [];
  const bParts = b.match(/[a-zA-Z]+|\d+/g) || [];

  // Compare each part in sequence
  const minLength = Math.min(aParts.length, bParts.length);

  for (let i = 0; i < minLength; i++) {
    const aIsNumber = /^\d+$/.test(aParts[i]);
    const bIsNumber = /^\d+$/.test(bParts[i]);

    // If both are numbers, compare them numerically
    if (aIsNumber && bIsNumber) {
      const diff = parseInt(aParts[i], 10) - parseInt(bParts[i], 10);
      if (diff !== 0) {
        return diff;
      }
    }
    // If both are strings, compare them alphabetically
    else if (!aIsNumber && !bIsNumber) {
      const diff = aParts[i].localeCompare(bParts[i]);
      if (diff !== 0) {
        return diff;
      }
    }
    // Numbers come before strings
    else {
      return aIsNumber ? -1 : 1;
    }
  }

  // If all parts were equal so far, the shorter one comes first
  return aParts.length - bParts.length;
};

/**
 * Sorts an array of objects by a specified string property using natural sort
 *
 * @param array Array of objects to sort
 * @param key Property name to sort by (must be a string property)
 * @returns New sorted array without modifying the original
 */
export const sortAlphaNum = <T extends object>(
  array: T[],
  key: keyof T,
): T[] => {
  return [...array].sort((a, b) => {
    const aValue = String(a[key]);
    const bValue = String(b[key]);
    return compareAlphaNum(aValue, bValue);
  });
};
